import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Plus, X, Info } from 'lucide-react';

export type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface ScheduleSlot {
  days: DayKey[];
  startHour: number; // 0-23
  startMinute: number; // 0,15,30,45
  endHour: number; // 0-23 — pra cobrir até "fim do dia" use 23 + endMinute 45
  endMinute: number; // 0,15,30,45
  /** Google-only: bid adjustment em % (-100..+900). Default 0 = sem ajuste. */
  bidAdjustment?: number;
}

export interface AdScheduleConfig {
  enabled: boolean;
  slots: ScheduleSlot[];
}

export const DEFAULT_AD_SCHEDULE: AdScheduleConfig = {
  enabled: false,
  slots: [],
};

interface Props {
  value: AdScheduleConfig;
  onChange: (v: AdScheduleConfig) => void;
  platforms: string[];
}

const AdScheduleInput: React.FC<Props> = ({ value, onChange, platforms }) => {
  const { t } = useTranslation();
  const hasGoogle = platforms.includes('google');
  const hasMeta = platforms.includes('meta');
  const hasLinkedIn = platforms.includes('linkedin');

  // LinkedIn API NÃO suporta dayparts (dia/hora). Doc oficial Microsoft Learn
  // confirma: só `runSchedule {start, end}` timestamps absolutos. Esconde o
  // componente inteiro se LinkedIn for a ÚNICA plataforma selecionada.
  const onlyLinkedIn = hasLinkedIn && !hasGoogle && !hasMeta;
  if (onlyLinkedIn) {
    return (
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-800 dark:text-blue-300">
          <p className="font-semibold mb-1">{t('campaigns.schedule.linkedinOnly.title', 'LinkedIn não suporta agendamento por dia/hora')}</p>
          <p>{t('campaigns.schedule.linkedinOnly.body', 'A API do LinkedIn não tem dayparting — só permite definir data inicial e final da campanha (não horário do dia nem dia da semana). Se quiser controle granular, considere adicionar Google ou Meta na campanha.')}</p>
        </div>
      </div>
    );
  }

  const ALL_DAYS: { key: DayKey; label: string }[] = [
    { key: 'monday', label: t('campaigns.schedule.dayShort.mon', 'Seg') },
    { key: 'tuesday', label: t('campaigns.schedule.dayShort.tue', 'Ter') },
    { key: 'wednesday', label: t('campaigns.schedule.dayShort.wed', 'Qua') },
    { key: 'thursday', label: t('campaigns.schedule.dayShort.thu', 'Qui') },
    { key: 'friday', label: t('campaigns.schedule.dayShort.fri', 'Sex') },
    { key: 'saturday', label: t('campaigns.schedule.dayShort.sat', 'Sáb') },
    { key: 'sunday', label: t('campaigns.schedule.dayShort.sun', 'Dom') },
  ];

  const setEnabled = (enabled: boolean) => {
    if (enabled && value.slots.length === 0) {
      // Cria slot padrão "Seg-Sex 9-18h" ao habilitar
      onChange({
        enabled: true,
        slots: [{
          days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          startHour: 9, startMinute: 0, endHour: 18, endMinute: 0, bidAdjustment: 0,
        }],
      });
    } else {
      onChange({ ...value, enabled });
    }
  };

  // Helper: representa "até o fim do dia" sem usar 24 (que vira 00 e buga).
  // Hora máxima válida é 23:45.
  const FULL_DAY_END = { endHour: 23, endMinute: 45 };

  const updateSlot = (i: number, partial: Partial<ScheduleSlot>) => {
    const slots = [...value.slots];
    slots[i] = { ...slots[i], ...partial };
    onChange({ ...value, slots });
  };

  const removeSlot = (i: number) => {
    const slots = value.slots.filter((_, idx) => idx !== i);
    onChange({ ...value, slots, enabled: slots.length > 0 ? value.enabled : false });
  };

  const addSlot = () => {
    onChange({
      ...value,
      enabled: true,
      slots: [...value.slots, {
        days: ['saturday', 'sunday'],
        startHour: 10, startMinute: 0, endHour: 16, endMinute: 0, bidAdjustment: 0,
      }],
    });
  };

  // Ajusta endHour se passou de 23 (depois do switch de input pra max 23)
  const sanitizeEndHour = (h: number): number => Math.max(0, Math.min(23, h));

  const toggleDay = (i: number, day: DayKey) => {
    const slot = value.slots[i];
    const days = slot.days.includes(day) ? slot.days.filter(d => d !== day) : [...slot.days, day];
    if (days.length === 0) return; // não permite slot sem dia
    updateSlot(i, { days });
  };

  const applyPreset = (preset: 'weekdays' | 'weekend' | 'allWeek' | 'businessHours') => {
    // FULL_DAY_END = 23:45 (Google/Meta/LinkedIn não aceitam 24:00)
    const slot = (cfg: Partial<ScheduleSlot>): ScheduleSlot => ({
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      startHour: 0, startMinute: 0, endHour: 23, endMinute: 45, bidAdjustment: 0,
      ...cfg,
    });
    if (preset === 'weekdays') {
      onChange({ enabled: true, slots: [slot({ startHour: 0, ...FULL_DAY_END })] });
    } else if (preset === 'weekend') {
      onChange({ enabled: true, slots: [slot({ days: ['saturday', 'sunday'], startHour: 0, ...FULL_DAY_END })] });
    } else if (preset === 'allWeek') {
      onChange({ enabled: false, slots: [] }); // 24/7 = sem schedule
    } else if (preset === 'businessHours') {
      onChange({ enabled: true, slots: [slot({ startHour: 9, endHour: 18, endMinute: 0 })] });
    }
  };

  // Hour options: 00 a 23 (24 não é válido — vira 00 e buga). Pra "fim do dia"
  // o user usa 23:45 que cobre o dia praticamente todo.
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  const minuteOptions = [0, 15, 30, 45];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
            <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('campaigns.schedule.title', 'Agendamento de veiculação')}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('campaigns.schedule.subtitle', 'Defina dias e horários em que sua campanha vai rodar')}
            </p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={value.enabled && value.slots.length > 0}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer peer-focus:ring-2 peer-focus:ring-primary-300 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
        </label>
      </div>

      {!value.enabled || value.slots.length === 0 ? (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            {t('campaigns.schedule.allTime', 'Veiculação 24h por dia, todos os dias da semana (padrão).')}
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => applyPreset('businessHours')} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
              {t('campaigns.schedule.preset.businessHours', 'Seg-Sex, 9h às 18h')}
            </button>
            <button onClick={() => applyPreset('weekdays')} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
              {t('campaigns.schedule.preset.weekdays', 'Só dias úteis')}
            </button>
            <button onClick={() => applyPreset('weekend')} className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
              {t('campaigns.schedule.preset.weekend', 'Só fins de semana')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {value.slots.map((slot, i) => (
            <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  {t('campaigns.schedule.slot', 'Horário')} #{i + 1}
                </span>
                {value.slots.length > 1 && (
                  <button onClick={() => removeSlot(i)} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Dias da semana */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  {t('campaigns.schedule.days', 'Dias')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_DAYS.map(({ key, label }) => {
                    const active = slot.days.includes(key);
                    return (
                      <button
                        key={key}
                        onClick={() => toggleDay(i, key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          active
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Horário */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    {t('campaigns.schedule.from', 'Início')}
                  </label>
                  <div className="flex gap-1">
                    <select value={slot.startHour} onChange={(e) => updateSlot(i, { startHour: parseInt(e.target.value) })}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      {hourOptions.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>)}
                    </select>
                    <select value={slot.startMinute} onChange={(e) => updateSlot(i, { startMinute: parseInt(e.target.value) })}
                      className="w-20 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      {minuteOptions.map(m => <option key={m} value={m}>:{String(m).padStart(2, '0')}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    {t('campaigns.schedule.to', 'Fim')}
                  </label>
                  <div className="flex gap-1">
                    <select value={sanitizeEndHour(slot.endHour)} onChange={(e) => updateSlot(i, { endHour: parseInt(e.target.value) })}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      {hourOptions.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>)}
                    </select>
                    <select value={slot.endMinute} onChange={(e) => updateSlot(i, { endMinute: parseInt(e.target.value) })}
                      className="w-20 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      {minuteOptions.map(m => <option key={m} value={m}>:{String(m).padStart(2, '0')}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Bid adjustment (só Google) */}
              {hasGoogle && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    {t('campaigns.schedule.bidAdj', 'Ajuste de lance (Google Ads)')}: <span className="font-semibold text-gray-900 dark:text-white">{slot.bidAdjustment === 0 || !slot.bidAdjustment ? t('campaigns.schedule.noAdj', 'Sem ajuste') : `${slot.bidAdjustment > 0 ? '+' : ''}${slot.bidAdjustment}%`}</span>
                  </label>
                  <input type="range" min={-90} max={300} step={10} value={slot.bidAdjustment || 0}
                    onChange={(e) => updateSlot(i, { bidAdjustment: parseInt(e.target.value) })}
                    className="w-full" />
                </div>
              )}
            </div>
          ))}

          <button onClick={addSlot} className="w-full px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border-2 border-dashed border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" />
            {t('campaigns.schedule.addSlot', 'Adicionar outro horário')}
          </button>
        </div>
      )}

      {/* Avisos por plataforma */}
      {value.enabled && value.slots.length > 0 && hasMeta && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <strong>{t('campaigns.schedule.metaNotice.title', 'Atenção Meta:')}</strong>{' '}
            {t('campaigns.schedule.metaNotice.body', 'O agendamento por dia/horário no Meta exige orçamento total (lifetime), não diário. Se a campanha estiver com orçamento diário, o Meta vai ignorar essa configuração.')}
          </div>
        </div>
      )}
      {value.enabled && value.slots.length > 0 && hasLinkedIn && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-800 dark:text-blue-300">
            <strong>{t('campaigns.schedule.linkedinNotice.title', 'LinkedIn:')}</strong>{' '}
            {t('campaigns.schedule.linkedinNotice.body', 'a API do LinkedIn não suporta agendamento por dia/hora. Esta configuração será aplicada apenas em Google e Meta — no LinkedIn a campanha rodará 24/7 dentro das datas inicial e final.')}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdScheduleInput;
